// src/pages/TestResult.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchTestResult } from "../api";
import RingChart from "../components/RingChart";

export default function TestResult() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");

  const raw = sessionStorage.getItem("testResult");
  const initialResult = raw ? JSON.parse(raw) : null;
  const sessionId = initialResult?.sessionId || initialResult?._id;

  useEffect(() => {
    if (sessionId) {
      fetchTestResult(sessionId)
        .then(d  => { setSession(d); setLoading(false); })
        .catch(e => { setError("Could not load review details: " + e.message); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [sessionId]);

  if (loading) return <div className="loading"><div className="spinner" /><span>Analyzing results…</span></div>;

  const r = session || initialResult;

  if (!r) return (
    <div className="loading">
      <p>No result found.</p>
      <button className="btn btn-dark btn-sm" onClick={() => navigate("/test")}>Take a test</button>
    </div>
  );

  const { correct = 0, wrong = 0, attempted = 0, totalQuestions = 0, accuracy = 0, score = 0, topicBreakdown = [], responses = [] } = r;
  const pct = Math.round(accuracy * 100);
  const msg = pct >= 75 ? "Excellent work! 🎉" : pct >= 50 ? "Good effort! 💪" : "Keep practising! 🔄";

  return (
    <div style={{ maxWidth: 850, margin: "0 auto" }}>
      {/* Summary Section */}
      <div className="card" style={{ padding: "30px 20px", textAlign: "center", marginBottom: 20 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 20 }}>
          <RingChart pct={pct} size={120} strokeWidth={10} />
        </div>
        <h2 style={{ fontSize: 24, fontWeight: 800, color: "var(--text-primary)", marginBottom: 8 }}>{msg}</h2>
        <p style={{ fontSize: 15, color: "var(--text-secondary)" }}>
          You scored <strong>{pct}%</strong> &middot; {correct}/{totalQuestions} correct answers
        </p>
        <div style={{ display: "inline-block", background: "var(--bg)", border: "1px solid var(--border)", borderRadius: 12, padding: "8px 16px", marginTop: 16, fontSize: 13, fontWeight: 600, color: "var(--blue)" }}>
          Marks: {typeof score === "number" ? score.toFixed(2) : score}
        </div>
      </div>

      {/* Stats row */}
      <div className="result-stats" style={{ marginBottom: 24 }}>
        {[["Correct", correct, "var(--green)"], ["Wrong", wrong, "var(--red)"], ["Unattempted", totalQuestions - attempted, "var(--text-secondary)"]].map(([l, v, c]) => (
          <div className="rs" key={l} style={{ flex: 1, textAlign: "center", padding: 15, background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12 }}>
            <div style={{ fontSize: 24, fontWeight: 800, color: c }}>{v}</div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontWeight: 500, marginTop: 4 }}>{l}</div>
          </div>
        ))}
      </div>

      {/* Review Section */}
      <h3 style={{ fontSize: 18, fontWeight: 800, marginBottom: 16 }}>Review Your Answers</h3>
      
      {responses.length === 0 ? (
        <div className="card" style={{ textAlign: "center", padding: 40 }}>
          <p style={{ color: "var(--text-muted)" }}>Detailed review not available for this session.</p>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {responses.map((resp, i) => {
            const q = resp.questionId;
            if (!q) return null; // Should not happen with population
            
            const isCorrect = resp.isCorrect;
            const isUnattempted = !resp.isAttempted;
            
            return (
              <div key={i} className="card" style={{ padding: 20, borderLeft: `6px solid ${isUnattempted ? "var(--border)" : isCorrect ? "var(--green)" : "var(--red)"}` }}>
                {/* Meta */}
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
                  <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)" }}>Question {i + 1} &middot; {q.topic}</span>
                  <span className={`tag ${isCorrect ? "tag-easy" : "tag-hard"}`} style={{ 
                    background: isUnattempted ? "var(--bg)" : isCorrect ? "var(--green-bg)" : "var(--red-bg)",
                    color: isUnattempted ? "var(--text-muted)" : isCorrect ? "var(--green-dark)" : "var(--red-dark)"
                  }}>
                    {isUnattempted ? "Not Attempted" : isCorrect ? "Correct" : "Incorrect"}
                  </span>
                </div>

                {/* Text */}
                <div style={{ fontSize: 15, color: "var(--text-primary)", fontWeight: 500, lineHeight: 1.6, marginBottom: 16 }}>
                  {q.questionText}
                </div>

                {/* Answer status */}
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 16 }}>
                  <div style={{ padding: 12, borderRadius: 8, background: "var(--bg)", border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>Your Answer</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: isUnattempted ? "var(--text-muted)" : isCorrect ? "var(--green-dark)" : "var(--red-dark)" }}>
                      {isUnattempted ? "None" : q.options[resp.selectedOptionIndex]}
                    </div>
                  </div>
                  <div style={{ padding: 12, borderRadius: 8, background: "var(--bg)", border: "1px solid var(--border)" }}>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textTransform: "uppercase", marginBottom: 4 }}>Correct Answer</div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: "var(--green-dark)" }}>
                      {q.options[q.correctOptionIndex]}
                    </div>
                  </div>
                </div>

                {/* Explanation */}
                {q.explanation && (
                  <div style={{ padding: "12px 14px", borderRadius: 8, background: "var(--blue-bg)", color: "var(--blue-dark)", fontSize: 13, lineHeight: 1.6 }}>
                    <strong>Explanation:</strong> {q.explanation}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Buttons */}
      <div style={{ display: "flex", gap: 12, margin: "30px 0 50px", flexWrap: "wrap" }}>
        <button className="btn btn-dark" style={{ flex: 1 }} onClick={() => navigate("/")}>Back to Dashboard</button>
        <button className="btn" style={{ flex: 1 }} onClick={() => navigate("/practice")}>Continue Practice</button>
        <button className="btn btn-blue" style={{ flex: 1 }} onClick={() => navigate("/test")}>Retake Test</button>
      </div>
    </div>
  );
}
